import React from 'react';
import { Search } from 'lucide-react';
import { BASKET_CATEGORY, RISK_LEVEL } from '../../constants/tradingConstants';
import { isWhiteLabel } from '../../config/tenantConfig';

interface FilterState {
  category?: string;
  risk_level?: string;
  search?: string;
}

interface StrategyFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
}

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: BASKET_CATEGORY.CONSERVATIVE, label: 'Conservative' },
  { value: BASKET_CATEGORY.AGGRESSIVE, label: 'Aggressive' },
  { value: BASKET_CATEGORY.HEDGED, label: 'Hedged' },
  { value: BASKET_CATEGORY.INCOME, label: 'Income' },
  { value: BASKET_CATEGORY.MOMENTUM, label: 'Momentum' },
];

const RISK_LEVELS = ['ALL', RISK_LEVEL.LOW, RISK_LEVEL.MEDIUM, RISK_LEVEL.HIGH] as const;

const StrategyFilters: React.FC<StrategyFiltersProps> = ({ filters, onFilterChange }) => {
  const wl = isWhiteLabel();
  const accentRing = wl ? 'focus:ring-zebu-500' : 'focus:ring-blue-500';
  const accentActive = wl ? 'bg-zebu-600 text-white' : 'bg-blue-600 text-white';
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
      {/* Search */}
      <div className="relative w-full sm:w-64">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search baskets..."
          value={filters.search || ''}
          onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
          className={`w-full h-9 pl-9 pr-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 ${accentRing} focus:border-transparent`}
        />
      </div>

      {/* Category dropdown */}
      <select
        value={filters.category || ''}
        onChange={(e) => onFilterChange({ ...filters, category: e.target.value })}
        className={`h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 ${accentRing} focus:border-transparent`}
      >
        {CATEGORIES.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>

      {/* Risk level chips */}
      <div className="flex items-center gap-1.5">
        {RISK_LEVELS.map((level) => {
          const active = level === 'ALL'
            ? !filters.risk_level
            : filters.risk_level === level;

          return (
            <button
              key={level}
              type="button"
              onClick={() => onFilterChange({ ...filters, risk_level: level === 'ALL' ? undefined : level })}
              className={`h-8 px-3 rounded-full text-xs font-medium transition-colors ${
                active
                  ? accentActive
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {level}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StrategyFilters;
